/* eslint-disable import/no-unresolved */
import {
  KlevuConfig,
  KlevuFetch,
  search,
  suggestions,
  searchCategory,
  searchCms,
} from 'https://cdn.skypack.dev/-/@klevu/core@v5.2.3-HNRf1HrJ9AqvnoHcX5mg/dist=es2019,mode=imports,min/optimized/@klevu/core.js'
const { debounce } = window.BAO.Utils

KlevuConfig.init({
  apiKey: `${window.theme.klevu.apiKey}`,
  url: `https://${window.theme.klevu.url}/cs/v2/search`,
})

class QuickSearch extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.loadQuickSearch()
  }

  setupListeners () {
    super.setupListeners()

    this.loadQuickSearch = this.loadQuickSearch.bind(this)

    this.loadResults = this.loadResults.bind(this)
    this.setupResults = this.setupResults.bind(this)
    this.onEmptyInput = this.onEmptyInput.bind(this)
    this.onNoResults = this.onNoResults.bind(this)
    this.onClose = this.onClose.bind(this)

    this.onFocus = this.onFocus.bind(this)
    this.onBlur = this.onBlur.bind(this)

    this.onPerformSearch = debounce(this.onPerformSearch.bind(this), 300)

    this.onTrigger = this.onTrigger.bind(this)
    this.externalEls.trigger?.element?.addEventListener(
      'click',
      this.onTrigger,
    )

    this.listeners.add(
      this?.externalEls?.input?.element,
      'focus',
      this.onFocus,
    )

    this.listeners.add(this?.externalEls?.input?.element, 'blur', this.onBlur)

    this?.externalEls?.input?.elements.forEach((externalInputEl) => {
      this.listeners.add(externalInputEl, 'keyup', this.onPerformSearch)
    })

    this.listeners.add(this?.externalEls.close?.element, 'click', this.onClose)

    if (!this.els?.backdrop?.exists) return
    this.listeners.add(this?.els?.backdrop?.element, 'click', this.onClose)
  }

  onTrigger () {
    this.focusAndOpenKeyboard()
  }

  loadQuickSearch () {
    return window.BAO.sectionRenderer
      .fetchSingle('quick-search')
      .then((res) => {
        const newDOM = window.BAO.sectionRenderer.convertToDOM(res)
        const myself = newDOM.querySelector('section')

        if (myself) {
          this.els.container.element.innerHTML = `${myself.outerHTML}`
        }
      })
      .catch(() => {
        return null
      })
  }

  hideSuggestionsAnimation () {
    if (
      this.externalEls.input.element.hasAttribute('data-quick-search-animated')
    ) {
      const animatedTextEl = this.externalEls.animatedText.element

      animatedTextEl.setAttribute('aria-hidden', 'true')
    }
  }

  onFocus () {
    this.setAttribute('aria-hidden', 'false')
    document.body.classList.toggle('util-SiteOverflowed', true)

    this.hideSuggestionsAnimation()
  }

  onBlur () {
    if (
      this.externalEls.input.element.hasAttribute(
        'data-quick-search-animated',
      ) &&
      this.externalEls.input.element.value === ''
    ) {
      const animatedTextEl = this.externalEls.animatedText.element

      animatedTextEl.setAttribute('aria-hidden', 'false')
    }
  }

  onClose () {
    this.setAttribute('aria-hidden', 'true')

    this.onEmptyInput()

    this.externalEls.input.element.value = ''
    document.body.classList.toggle('util-SiteOverflowed', false)
  }

  onPerformSearch (event) {
    const searchTerm = event.target.value

    // Store the last active input element
    this.lastActiveInput = event.target

    if (searchTerm.length < 3) {
      this.onEmptyInput()
    } else {
      this.loadResults(searchTerm)
    }
  }

  onEmptyInput () {
    this.els['no-results'].element.setAttribute('aria-hidden', 'true')

    this.els.layout.elements.forEach((layoutEl) => {
      layoutEl.setAttribute('data-layout', 'default')
      layoutEl.setAttribute('aria-hidden', 'false')
    })

    this.els['suggestions-container'].element.setAttribute(
      'aria-hidden',
      'true',
    )

    this.els['default-suggestion'].elements.forEach((defaultSuggestionEl) => {
      defaultSuggestionEl.setAttribute('aria-hidden', 'false')
    })

    this.els['results-main'].element.setAttribute('aria-hidden', 'true')

    this.els['default-main'].element.setAttribute('aria-hidden', 'false')
  }

  onNoResults () {
    const searchTerm = this.lastActiveInput ? this.lastActiveInput.value : ''

    this.els.query.element.innerText = searchTerm

    this.els.layout.elements.forEach((layoutEl) => {
      layoutEl.setAttribute('aria-hidden', 'true')
    })

    this.els['no-results'].element.setAttribute('aria-hidden', 'false')
  }

  focusAndOpenKeyboard () {
    // Align temp input element approximately where the input element is
    // so the cursor doesn't jump around
    const tempInputEl = document.createElement('input')
    tempInputEl.style.position = 'absolute'
    tempInputEl.style.top = `${this.externalEls.input.element.offsetTop + 7}px`
    tempInputEl.style.left = `${this.externalEls.input.element.offsetLeft}px`
    tempInputEl.style.height = 0
    tempInputEl.style.opacity = 0
    // Put this temp element as a child of the page <body> and focus on it
    document.body.appendChild(tempInputEl)
    tempInputEl.focus()

    // The keyboard is open. Now do a delayed focus on the target element
    window.setTimeout(() => {
      this.externalEls.input.elements.forEach((externalInputEl) => {
        externalInputEl.focus()
        externalInputEl.click()
      })
    }, 500)
  }

  async loadResults (searchTerm) {
    const result = await KlevuFetch(
      suggestions(searchTerm, {
        id: 'suggestions', // not required. as default it's the same.
      }),
      searchCategory(searchTerm, {
        id: 'category',
      }),
      searchCms(searchTerm, {
        id: 'page',
      }),
      search(searchTerm, {
        id: 'search', // not required as for default it's the same.
        fields: ['url'],
      }),
    )

    const suggestionRecords =
      result.suggestionsById('suggestions')?.suggestions

    const categoryRecords = result.queriesById('category')?.records

    const pageRecords = result.queriesById('page')?.records

    const records = result.queriesById('search')?.records
    const recordsTotal = result.queriesById('search').meta.totalResultsFound

    if (records) {
      this.setupResults(records, recordsTotal, searchTerm)
    }

    this.setupSuggestions(suggestionRecords, categoryRecords, pageRecords)
  }

  async setupSuggestions (suggestionRecords, categoryRecords, pageRecords) {
    const suggestionHTML =
      suggestionRecords?.length > 0
        ? `<div class="sec-QuickSearch_SuggestionGroup"><h3 class="sec-QuickSearch_SuggestionTitle fz-20_130">${window.klevuStrings.suggestions}</h3><div class="sec-QuickSearch_SuggestionButtons">${await this.getSuggestionHTML(suggestionRecords)}</div></div>`
        : ''

    const categoryHTML =
      categoryRecords?.length > 0
        ? `<div class="sec-QuickSearch_SuggestionGroup"><h3 class="sec-QuickSearch_SuggestionTitle fz-20_130">${window.klevuStrings.collections}</h3><div class="sec-QuickSearch_SuggestionButtons">${await this.getCategoryHTML(categoryRecords)}</div></div>`
        : ''

    const pageHTML =
      pageRecords?.length > 0
        ? `<div class="sec-QuickSearch_SuggestionGroup"><h3 class="sec-QuickSearch_SuggestionTitle fz-20_130">${window.klevuStrings.pages}</h3><div class="sec-QuickSearch_SuggestionButtons">${await this.getPageHTML(pageRecords)}</div></div>`
        : ''

    // Combine all groups into the suggestions container
    this.els.suggestions.element.innerHTML = `<div class="sec-QuickSearch_SuggestionGroups">${suggestionHTML}${categoryHTML}${pageHTML}</div>`

    const hasAnyRecords =
      suggestionRecords.length > 0 ||
      categoryRecords.length > 0 ||
      pageRecords.length > 0

    this.els['suggestions-container'].element.setAttribute(
      'aria-hidden',
      hasAnyRecords ? 'false' : 'true',
    )

    this.els['default-suggestion'].elements.forEach((defaultSuggestionEl) => {
      defaultSuggestionEl.setAttribute(
        'aria-hidden',
        hasAnyRecords ? 'true' : 'false',
      )
    })
  }

  async getCategoryHTML (categoryRecords) {
    if (!categoryRecords || categoryRecords.length === 0) return ''

    const categoryHTML = await Promise.all(
      categoryRecords.map((category) => {
        return `
          <a href="${category.url}" class="sec-QuickSearch_SuggestionButton fz-13_130">
            ${category.name}
          </a>
        `
      }),
    )

    return categoryHTML.filter(Boolean).join('')
  }

  async getPageHTML (pageRecords) {
    if (!pageRecords || pageRecords.length === 0) return ''

    const pageHTML = await Promise.all(
      pageRecords.map((page) => {
        return `
          <a href="${page.url}" class="sec-QuickSearch_SuggestionButton fz-13_130">
            ${page.name}
          </a>
        `
      }),
    )

    return pageHTML.filter(Boolean).join('')
  }

  async getSuggestionHTML (suggestionRecords) {
    const stripHtml = (html) => {
      return html.replace(/<\/?[^>]+(>|$)/g, '')
    }

    const suggestionHTML = await Promise.all(
      suggestionRecords.map((suggestion) => {
        const strippedSuggestion = stripHtml(suggestion.suggest)

        return `
          <a href="${window.Shopify.routes.root}pages/search-results?q=${strippedSuggestion}" class="sec-QuickSearch_SuggestionButton fz-13_130">
            ${suggestion.suggest}
          </a>
        `
      }),
    )

    return suggestionHTML.filter(Boolean).join('')
  }

  async setupResults (records, recordsTotal, searchTerm) {
    if (records.length < 1) {
      this.onNoResults()

      return
    } else {
      this.els.layout.elements.forEach((layoutEl) => {
        layoutEl.setAttribute('aria-hidden', 'false')
      })

      this.els['no-results'].element.setAttribute('aria-hidden', 'true')
    }

    this.setAttribute('aria-busy', 'true')

    const productCardHTML = await this.getAllProductCardHTML(records)
    this.els.results.element.innerHTML = productCardHTML

    this.els.count.element.textContent = `(${recordsTotal})`

    const viewAllHTML = `<a href='${window.Shopify.routes.root}pages/search-results?q=${searchTerm}' class="btn-Button btn-Button-secondary">
      ${window.klevuStrings.viewAll}

      <span class="btn-Button_Icon">
        <svg viewBox="0 0 6 9" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M.754 7.9.4 8.252l.707.707.354-.354L4.857 5.21l.354-.354.353-.353-.353-.354-.354-.353L1.461.399 1.107.046.4.753l.354.353L4.15 4.503.754 7.899Z" fill="currentColor"/>
        </svg>
      </span>
    </a>`

    this.els['view-all'].element.innerHTML = viewAllHTML

    this.els['results-main'].element.setAttribute(
      'aria-hidden',
      records.length < 1 ? 'true' : 'false',
    )

    this.els['default-main'].element.setAttribute(
      'aria-hidden',
      records.length > 0 ? 'true' : 'false',
    )

    this.els.layout.elements.forEach((layoutEl) => {
      layoutEl.setAttribute('data-layout', 'results')
    })

    this.setAttribute('aria-busy', 'false')
  }

  async getAllProductCardHTML (records) {
    const productCardHTML = await Promise.all(
      records.map(
        async (product, index) => await this.getProductCardHTML(product, index),
      ),
    )
    return productCardHTML.filter(Boolean).join('')
  }

  async getProductCardHTML (product, index) {
    const productUrl = product.url.replace(
      '/products/',
      `${window.Shopify.routes.root}products/`,
    )

    return window.BAO.sectionRenderer
      .fetchSingle('search-product-card', `${productUrl}`)
      .then((res) => {
        const newDOM = window.BAO.sectionRenderer.convertToDOM(res)
        const myself = newDOM.querySelector('article')

        if (myself) {
          return `<div class="src-Search_Result">${myself.outerHTML}</div>`
        }
      })
      .catch(() => {
        return null
      })
  }
}

if (!customElements.get('quick-search')) {
  customElements.define('quick-search', QuickSearch)
}
